import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Categories.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

const TABS = [
  { key: 'item',    label: 'Item Categories' },
  { key: 'expense', label: 'Expense Item Categories' },
  { key: 'product', label: 'Product Categories' },
];

/* ── Category / Subcategory modal ────────────────────────────── */
function CatModal({ title, initial, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    status: initial?.status || 'A',
    ...(categories ? { category_id: initial?.category_id ?? categories[0]?.id ?? '' } : {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="cat-modal-overlay" onClick={onClose}>
      <div className="cat-modal" onClick={e => e.stopPropagation()}>
        <div className="cat-modal-header">
          <h3>{title}</h3>
          <button className="cat-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="cat-error">{error}</div>}
          {categories && (
            <div className="cat-form-group">
              <label>Category *</label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="cat-form-group">
            <label>Name *</label>
            <input
              autoFocus
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Enter name"
            />
          </div>
          <div className="cat-form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Optional description"
            />
          </div>
          <div className="cat-form-group">
            <label className="cat-checkbox-label">
              <input
                type="checkbox"
                checked={form.status === 'A'}
                onChange={e => set('status', e.target.checked ? 'A' : 'I')}
              />
              Active
            </label>
          </div>
          <div className="cat-modal-footer">
            <button type="button" className="cat-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="cat-btn-save" disabled={saving}>
              {saving ? 'Saving…' : initial ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Category panel ───────────────────────────────────────────── */
function CategoryPanel({ type, token, selectedCat, onSelect, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | { row: null|row }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/categories`, {
        params: { type },
        headers: authHdr(token),
      });
      setRows(r.data);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [type, token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const row = modal.row;
    if (row) {
      await axios.put(`${API}/categories/${row.id}`, form, { params: { type }, headers: authHdr(token) });
    } else {
      await axios.post(`${API}/categories`, form, { params: { type }, headers: authHdr(token) });
    }
    setModal(null);
    await load();
    onChanged?.();
  };

  const visible = rows.filter(r =>
    !search.trim() || r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="cat-panel">
      <div className="cat-panel-header">
        <h3>Categories</h3>
        <button className="cat-add-btn" onClick={() => setModal({ row: null })}>+ Add</button>
      </div>
      <div className="cat-search">
        <input
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="cat-list">
        {loading && <div className="cat-loading">Loading…</div>}
        {!loading && visible.length === 0 && <div className="cat-empty">No categories found.</div>}
        {visible.map(row => (
          <div
            key={row.id}
            className={`cat-row${selectedCat?.id === row.id ? ' selected' : ''}`}
            onClick={() => onSelect(row)}
          >
            <span className="cat-row-name">{row.name}</span>
            <span className="cat-row-desc">{row.description || ''}</span>
            <span className={`cat-status cat-status--${row.status === 'A' ? 'active' : 'inactive'}`}>
              {row.status === 'A' ? 'Active' : 'Inactive'}
            </span>
            <div className="cat-row-actions" onClick={e => e.stopPropagation()}>
              <button className="cat-edit-btn" onClick={() => setModal({ row })}>Edit</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <CatModal
          title={modal.row ? `Edit — ${modal.row.name}` : 'Add Category'}
          initial={modal.row}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ── Subcategory panel ────────────────────────────────────────── */
function SubcategoryPanel({ type, token, category }) {
  const [rows, setRows] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`, { params: { type }, headers: authHdr(token) })
      .then(r => setAllCats(r.data))
      .catch(() => {});
  }, [type, token]);

  const load = useCallback(async () => {
    if (!category) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/categories/${category.id}/subs`, {
        params: { type },
        headers: authHdr(token),
      });
      setRows(r.data);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [type, token, category]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const row = modal.row;
    if (row) {
      await axios.put(`${API}/categories/sub/${row.id}`, form, { params: { type }, headers: authHdr(token) });
    } else {
      await axios.post(`${API}/categories/${category.id}/subs`, form, { params: { type }, headers: authHdr(token) });
    }
    setModal(null);
    await load();
  };

  const visible = rows.filter(r =>
    !search.trim() || r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="cat-panel">
      <div className="cat-panel-header">
        <h3>
          {category ? `Subcategories — ${category.name}` : 'Subcategories'}
        </h3>
        {category
          ? <button className="cat-add-btn" onClick={() => setModal({ row: null })}>+ Add</button>
          : <span className="cat-panel-hint">Select a category</span>}
      </div>
      {category && (
        <div className="cat-search">
          <input
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}
      <div className="cat-list">
        {!category && <div className="cat-empty">Click a category on the left to view its subcategories.</div>}
        {category && loading && <div className="cat-loading">Loading…</div>}
        {category && !loading && visible.length === 0 && <div className="cat-empty">No subcategories yet.</div>}
        {visible.map(row => (
          <div key={row.id} className="cat-row">
            <span className="cat-row-name">{row.name}</span>
            <span className="cat-row-desc">{row.description || ''}</span>
            <span className={`cat-status cat-status--${row.status === 'A' ? 'active' : 'inactive'}`}>
              {row.status === 'A' ? 'Active' : 'Inactive'}
            </span>
            <div className="cat-row-actions">
              <button className="cat-edit-btn" onClick={() => setModal({ row })}>Edit</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <CatModal
          title={modal.row ? `Edit — ${modal.row.name}` : `Add Subcategory to ${category.name}`}
          initial={modal.row}
          categories={modal.row ? allCats : undefined}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
const Categories = () => {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const [activeTab, setActiveTab] = useState('item');
  const [selectedCat, setSelectedCat] = useState(null);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedCat(null);
  };

  return (
    <div className="cat-page">
      <div className="cat-header">
        <h2>Categories</h2>
      </div>

      <div className="cat-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`cat-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="cat-split">
        <CategoryPanel
          key={activeTab}
          type={activeTab}
          token={token}
          selectedCat={selectedCat}
          onSelect={setSelectedCat}
          onChanged={() => setSelectedCat(null)}
        />
        <SubcategoryPanel
          key={`${activeTab}-${selectedCat?.id}`}
          type={activeTab}
          token={token}
          category={selectedCat}
        />
      </div>
    </div>
  );
};

export default Categories;
